/**
 *
 * @param {index} index index of singal
 * @param {object} signalsData
 * @param {function} setSignalsData
 * @desc: for sorting signals
 */
export const sortHandler = (index, signalsData, setSignalsData) => {
  const temp = [...signalsData].sort(function (a, b) {
    return a.signals[index].match === b.signals[index].match
      ? 0
      : a.signals[index].match
      ? -1
      : 1;
  });

  setSignalsData(temp);
};

/**
 * Sorts two objects by their timestamps in ascending order.
 * If both timestamps are null, the objects are considered equal.
 * If only one timestamp is null, the object with a null timestamp is put at the end.
 * Otherwise, the objects are sorted by their timestamps.
 * @param {Object} a The first object to compare.
 * @param {Object} b The second object to compare.
 * @return {number} A negative number if a should come before b, 0 if they are equal, and a positive number if a should come after b.
 */
export const sortByTimestamp = (a, b) => {
  // If both timestamps are null, consider them equal (return 0)
  if (a.timestamp === null && b.timestamp === null) {
    return 0;
  }

  // If only one timestamp is null, put the object with a null timestamp at the end
  if (a.timestamp === null) {
    return 1;
  } else if (b.timestamp === null) {
    return -1;
  }

  // Otherwise, sort by actual timestamps (ascending order)
  return a.timestamp - b.timestamp;
};

/**
 * Sorts two objects by the existence of a report for the given indicator value.
 * If object a has a report for the given indicator value and object b does not,
 * a comes first. If object b has a report for the given indicator value and
 * object a does not, b comes first. If neither object has a report for the
 * given indicator value, they are considered equal.
 * @param {Object} a The first object to compare.
 * @param {Object} b The second object to compare.
 * @param {string} indicatorValue The indicator value to sort by.
 * @return {number} A negative number if a should come before b, 0 if they are equal, and a positive number if a should come after b.
 */
export const sortBySpecificIndicator = (a, b, indicatorValue) => {
  const aIndicator = a.assets.find(
    (indicator) => indicator.indicator === indicatorValue
  );
  const bIndicator = b.assets.find(
    (indicator) => indicator.indicator === indicatorValue
  );

  const aHasReport = aIndicator && aIndicator.report !== null;
  const bHasReport = bIndicator && bIndicator.report !== null;

  if (aHasReport && !bHasReport) {
    return -1;
  } else if (!aHasReport && bHasReport) {
    return 1;
  } else {
    return 0;
  }
};

/**
 * The function `searchIndicators` filters a collection of indicators based on a search term and
 * categorizes the results.
 *
 * @param {object} indicators - An object containing categories of indicators.
 * @param {string} search - The search query to filter indicators by.
 *
 * @returns The `searchIndicators` function returns an object containing categories as keys and arrays
 * of filtered items as values. The filtered items are those that have a `name` property containing
 * the search term (case-insensitive).
 */
export const searchIndicators = (indicators, search) => {
  const results = {};

  for (let category in indicators) {
    const items = indicators[category];
    const filteredItems = items.filter((item) =>
      item.name.toLowerCase().includes(search.toLowerCase())
    );

    if (filteredItems.length > 0) {
      results[category] = filteredItems;
    }
  }

  return results;
};

/**
 * The function `transformData` takes an array of objects representing
 * data fetched from the API and transforms it into an array of objects
 * with a different structure that is easier to work with in the application.
 *
 * The new structure is as follows:
 * - The array is sorted in descending order by date.
 * - The objects no longer have a date property.
 * - Instead, each object has an array of indicators, each of which has a
 *   date property.
 * - The objects also have a timestamp, is_user_seen, report, and status
 *   property.
 *
 * @param {Array} data - The array of objects to transform.
 * @returns {Array} The transformed array of objects.
 */
export const transformData = (data) => {
  const groupedByIndicator = Object.entries(
    data.reduce((acc, asset) => {
      if (!acc[asset.indicator]) {
        acc[asset.indicator] = [];
      }

      acc[asset.indicator].push(asset);
      return acc;
    }, {})
  ).map(([indicator, assets]) => ({
    indicator,
    assets: assets?.sort((a, b) => {
      const dateA = a.last_matched_date ? new Date(a.last_matched_date) : null;
      const dateB = b.last_matched_date ? new Date(b.last_matched_date) : null;

      // Handle null dates by considering them as the oldest dates
      if (!dateA) return 1;
      if (!dateB) return -1;

      // Sort in descending order (newer to older)
      return dateB - dateA;
    }),
  }));

  return groupedByIndicator;
};

/**
 * Checks if the given status includes the substring "hot". If the status is undefined or null, it returns false.
 * @param {string} status - The status to be checked.
 * @returns {boolean} True if the status includes "hot", false if it does not.
 */
export const hasStatus = (status) => {
  if (status) {
    return status.includes("hot");
  } else {
    return false;
  }
};

/**
 * Parses a query string into an object containing key-value pairs.
 *
 * @param {string} queryString - The query string to parse (e.g., "key1=value1&key2=value2").
 * @returns {Object} An object representing the parsed key-value pairs from the query string.
 *
 * @example
 *  Example usage:
 * const query = "timeframe=1h&pair=btc&exchange=ex1&count=10&signal=None&page=beginner";
 * const params = parseQueryString(query);
 *
 *  Output:
 *  {
 *    timeframe: "1h",
 *    pair: "btc",
 *    exchange: "ex1",
 *    count: "10",
 *    signal: "None",
 *    page: "beginner"
 *  }
 */
export const parseQueryString = (queryString) => {
  const params = new URLSearchParams(queryString);

  const paramsObject = Object.fromEntries(params.entries());

  return paramsObject;
};

/**
 * Filters chart indicators based on a selected strategy or a specific indicator key.
 * @param {Array<Object>} indicators - An array of indicator objects, each containing a key.
 * @param {string} selectedIndicator - The key of the indicator to filter by when no strategy is selected.
 * @param {Array<Object>} strategies - An array of strategy objects, each containing an id and indicators.
 * @param {string} selectedStrategy - The id of the selected strategy to filter indicators by.
 * @returns {Array<Object>} An array of filtered indicator objects based on the selected strategy or indicator key.
 */
export const filterChartIndicators = (
  indicators,
  selectedIndicator,
  strategies,
  selectedStrategy
) => {
  let result = [];

  if (strategies && strategies.length > 0) {
    const filteredStrategyIndicators = strategies?.find(
      (str) => str.id === selectedStrategy
    );

    if (selectedStrategy) {
      result = filteredStrategyIndicators.indicators;
    }
  } else {
    result = indicators?.filter(
      (indicator) => indicator.key === selectedIndicator
    );
  }

  return result;
};

/**
 * Separates the digits of a given number string with commas.
 * @param {string} num The number string to separate
 * @returns {string | undefined} The separated string, or undefined if the argument is not a string
 */
export const separateDigits = (num) => {
  if (typeof num !== "string") return "Error: Argument should be a string.";

  const digits = num.split("").reverse();
  let result = "";

  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && i % 3 === 0) {
      result += ",";
    }
    result += digits[i];
  }

  return result.split("").reverse().join("");
};

/**
 * Checks if the user has permission to access a specific page based on the provided pages and route.
 *
 * @param {Array<Object>} pages - An array of page objects, where each object contains a `key` property.
 * @param {string} route - The route to check against the pages' keys.
 * @returns {boolean} - Returns `true` if the user has permission to access the page (i.e., if the route matches any page key), otherwise `false`.
 */
export const doesUserHavePermissionToAccessPage = (pages, route) => {
  return pages?.some((page) => page.key === route);
};

/**
 * Opens a chart in a new browser tab by creating an iframe that loads the provided chart data URL.
 *
 * @param {string} chartDataUrl - The URL of the chart data to be displayed in the new tab.
 * @returns {void}
 */
export const openCharInNewTab = (chartDataUrl) => {
  if (!chartDataUrl) return;

  const newTab = window.open("", "_blank");

  newTab.document.write(`
    <html>
      <head>
        <title>Ajna | Chart</title>
      </head>
      <body style="margin: 0; overflow: hidden;">
        
        <iframe id="chartIframe"  src="${chartDataUrl}" style="height: 100vh; width: 100vw; border: none;" />

      </body>
    </html>
  `);
};

/**
 * Sorts the indicators of each asset in the provided assets array by the order of the keys in the provided indicatorsList array.
 *
 * @param {Object[]} assets - An array of objects, where each object contains an `indicators` property.
 * @param {Object[]} indicatorsList - An array of objects, where each object contains a `key` property.
 * @returns {Object[]} - The sorted array of assets.
 */
export const sortAssetIndicators = (assets, indicatorsList) => {
  const keyOrder = {};

  indicatorsList?.forEach((item, index) => {
    keyOrder[item.key] = index;
  });

  assets?.forEach((asset) => {
    asset?.indicators?.sort((a, b) => {
      return keyOrder[a.indicator] - keyOrder[b.indicator];
    });
  });

  return assets;
};

export const ticketPriority = [
  { label: "کم", key: "low" },
  { label: "متوسط", key: "medium" },
  { label: "زیاد", key: "high" },
];

export const ticketUnitary = [
  { label: "پشتیبانی", key: "support" },
  { label: "مالی", key: "financial" },
  { label: "مدیریت", key: "management" },
  { label: "حقوقی", key: "legal support" },
];

export const statusTitle = [
  { key: "open", label: "باز" },
  { key: "closed", label: "بسته" },
  { key: "pending", label: "در انتظار" },
  { key: "answered", label: "پاسخ داده شده" },
];

export const breakpoints = {
  0: {
    slidesPerView: 1,
  },
  576: {
    slidesPerView: 2.3,
  },
  850: {
    slidesPerView: 2.8,
  },
  992: {
    slidesPerView: 3.3,
  },
  1200: {
    slidesPerView: 5,
  },
  1500: {
    slidesPerView: 6,
  },
  1750: {
    slidesPerView: 7,
  },
};
